/**
 * MockDataDialog — the Mock Data Generator tool (admin). Pick a connection /
 * database / table, infer a generator per column, review the FK cascade plan,
 * tweak generators, preview, then either insert (background job + ProgressModal)
 * or render INSERT SQL into a copyable viewer.
 */
import { createSignal, createResource, For, Show } from 'solid-js';
import { createStore } from 'solid-js/store';
import Modal from './Modal.jsx';
import ProgressModal from './ProgressModal.jsx';
import { api } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import { analyzeMockData, previewMockData, runMockData } from '../lib/mockdata.js';

export default function MockDataDialog(props) {
  const [conns] = createResource(async () => {
    try { return (await api.get('/api/connections')).connections || []; } catch { return []; }
  });
  const [connId, setConnId] = createSignal(0);
  const [database, setDatabase] = createSignal('');
  const [table, setTable] = createSignal('');

  const [dbs] = createResource(connId, async (id) => {
    if (!id) return [];
    try { return (await api.get(`/api/connections/${id}/databases`)).databases || []; } catch { return []; }
  });
  const [tables] = createResource(
    () => (connId() && database() ? [connId(), database()] : null),
    async ([id, db]) => {
      try { return (await api.get(`/api/connections/${id}/databases/${encodeURIComponent(db)}/objects`)).tables || []; }
      catch { return []; }
    },
  );

  const [count, setCount] = createSignal(100);
  const [parentCount, setParentCount] = createSignal(20);
  const [seed, setSeed] = createSignal(1);

  const [analysis, setAnalysis] = createSignal(null); // { plan, warnings, cycleError, availableKinds }
  const [cols, setCols] = createStore([]);            // editable per-column specs (target table)
  const [busy, setBusy] = createSignal(false);
  const [preview, setPreview] = createSignal(null);   // { columns, rows }
  const [runToken, setRunToken] = createSignal('');
  const [sqlOut, setSqlOut] = createSignal(null);     // { sql, rowCount }

  const baseBody = () => ({
    connectionId: Number(connId()) || 0,
    database: database(),
    table: table(),
    count: Number(count()) || 0,
    defaultParentCount: Number(parentCount()) || 0,
    seed: Number(seed()) || 0,
  });

  const analyze = async () => {
    if (!connId() || !database() || !table()) { toast.error('Pick a connection, database, and table.'); return; }
    setBusy(true);
    setPreview(null);
    try {
      const res = await analyzeMockData(baseBody());
      setAnalysis(res);
      setCols(res.columns || []);
    } catch (e) { toast.error('Analyze failed: ' + (e?.message || e)); }
    finally { setBusy(false); }
  };

  const cycle = () => analysis()?.cycleError || '';
  const kinds = () => analysis()?.availableKinds || [];

  const doPreview = async () => {
    setBusy(true);
    try {
      const res = await previewMockData({ ...baseBody(), columns: cols });
      setPreview(res);
    } catch (e) { toast.error('Preview failed: ' + (e?.message || e)); }
    finally { setBusy(false); }
  };

  const run = async (destination) => {
    if (cycle()) { toast.error('Resolve the FK cycle first.'); return; }
    setBusy(true);
    try {
      const res = await runMockData({ ...baseBody(), columns: cols, destination });
      if (destination === 'sql') setSqlOut(res);
      else { setRunToken(res.token); toast.success('Insert started.'); }
    } catch (e) { toast.error('Run failed: ' + (e?.message || e)); }
    finally { setBusy(false); }
  };

  const copySql = async () => {
    try { await navigator.clipboard.writeText(sqlOut().sql || ''); toast.success('SQL copied.'); }
    catch { toast.error('Copy failed — select and copy manually.'); }
  };

  const editable = (c) => !c.skip && !c.isFk;

  return (
    <Modal open={true} onClose={props.onClose} title="Mock Data Generator" wide>
      {/* Target picker */}
      <div class="settings-row"><label>Connection</label>
        <select class="input" value={connId()} onChange={(e) => { setConnId(Number(e.target.value)); setDatabase(''); setTable(''); setAnalysis(null); }}>
          <option value="0">— pick —</option>
          <For each={conns() || []}>{(c) => <option value={c.id}>{c.name}</option>}</For>
        </select></div>
      <Show when={connId() > 0}>
        <div class="settings-row"><label>Database</label>
          <select class="input" value={database()} onChange={(e) => { setDatabase(e.target.value); setTable(''); setAnalysis(null); }}>
            <option value="">— pick —</option>
            <For each={dbs() || []}>{(d) => <option value={d.name}>{d.name}</option>}</For>
          </select></div>
      </Show>
      <Show when={database()}>
        <div class="settings-row"><label>Table</label>
          <select class="input" value={table()} onChange={(e) => { setTable(e.target.value); setAnalysis(null); }}>
            <option value="">— pick —</option>
            <For each={tables() || []}>{(t) => <option value={t.name}>{t.name}</option>}</For>
          </select></div>
      </Show>

      <div class="settings-row"><label>Rows</label>
        <input class="input settings-num" type="number" min="1" value={count()} onInput={(e) => setCount(e.target.value)} />
        <span class="muted" style="font-size:11px;">rows to generate for the target table</span></div>
      <div class="settings-row"><label>Parent rows</label>
        <input class="input settings-num" type="number" min="1" value={parentCount()} onInput={(e) => setParentCount(e.target.value)} />
        <span class="muted" style="font-size:11px;">default rows for an empty FK parent that gets auto-generated</span></div>
      <div class="settings-row"><label>Seed</label>
        <input class="input settings-num" type="number" value={seed()} onInput={(e) => setSeed(e.target.value)} />
        <span class="muted" style="font-size:11px;">same seed → same data (reproducible)</span></div>

      <div style="display:flex;gap:8px;margin:6px 0 4px;">
        <button class="btn" disabled={busy() || !table()} onClick={analyze}>Analyze</button>
      </div>

      <Show when={analysis()}>
        {/* Cascade plan */}
        <div class="jobs-section-label">Generation plan</div>
        <Show when={cycle()}>
          <div class="run-err-text" style="margin:4px 0;">{cycle()}</div>
        </Show>
        <For each={analysis().warnings || []}>{(w) => <div class="muted" style="font-size:12px;color:var(--warn,#f59e0b);">⚠ {w}</div>}</For>
        <table class="data-table" style="margin:6px 0;">
          <thead><tr><th>Table</th><th>Action</th><th>Rows</th><th>Why</th></tr></thead>
          <tbody>
            <For each={analysis().plan || []}>
              {(s) => (
                <tr>
                  <td class="mono">{s.schema}.{s.table}</td>
                  <td>{s.generate ? <span class="badge badge-scheduled">generate</span> : <span class="muted">use existing</span>}</td>
                  <td class="mono">{s.count}</td>
                  <td class="muted" style="font-size:12px;">{s.reason}</td>
                </tr>
              )}
            </For>
          </tbody>
        </table>

        {/* Column generators (target table) */}
        <div class="jobs-section-label">Columns — {table()}</div>
        <table class="data-table">
          <thead><tr><th>Column</th><th>Type</th><th>Generator</th><th>NULL %</th><th>Unique</th></tr></thead>
          <tbody>
            <For each={cols}>
              {(c, i) => (
                <tr classList={{ 'job-disabled': c.skip }}>
                  <td class="mono">{c.name}</td>
                  <td class="muted mono" style="font-size:12px;">{c.type}</td>
                  <td>
                    <Show when={editable(c)} fallback={
                      <span class="muted">{c.skip ? `skipped — ${c.skipReason}` : c.isFk ? 'foreign key (from parent)' : c.kind}</span>
                    }>
                      <select class="input" style="max-width:170px;" value={c.kind} onChange={(e) => setCols(i(), 'kind', e.target.value)}>
                        <For each={kinds()}>{(k) => <option value={k}>{k}</option>}</For>
                      </select>
                    </Show>
                  </td>
                  <td>
                    <Show when={editable(c) && c.nullable}>
                      <input class="input settings-num" type="number" min="0" max="100" style="width:64px;"
                             value={c.nullPercent || 0} onInput={(e) => setCols(i(), 'nullPercent', Number(e.target.value) || 0)} />
                    </Show>
                  </td>
                  <td>
                    <Show when={editable(c)}>
                      <input type="checkbox" checked={c.unique} onChange={(e) => setCols(i(), 'unique', e.currentTarget.checked)} />
                    </Show>
                  </td>
                </tr>
              )}
            </For>
          </tbody>
        </table>

        {/* Actions */}
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:12px;flex-wrap:wrap;">
          <button class="btn btn-ghost" disabled={busy()} onClick={doPreview}>Preview</button>
          <button class="btn btn-ghost" disabled={busy() || !!cycle()} onClick={() => run('sql')}>Generate SQL</button>
          <button class="btn btn-primary" disabled={busy() || !!cycle()} onClick={() => run('insert')}>Insert into table</button>
        </div>

        {/* Preview grid */}
        <Show when={preview()}>
          <div class="jobs-section-label">Preview ({(preview().rows || []).length} rows)</div>
          <div class="data-table-wrap" style="max-height:260px;overflow:auto;">
            <table class="data-table">
              <thead><tr><For each={preview().columns || []}>{(c) => <th>{c}</th>}</For></tr></thead>
              <tbody>
                <For each={preview().rows || []}>
                  {(row) => (
                    <tr><For each={row}>{(cell) => <td class="mono" style="font-size:12px;">{cell === null ? <span class="muted">NULL</span> : cell}</td>}</For></tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>
      </Show>

      {/* Insert progress */}
      <Show when={runToken()}>
        <ProgressModal
          token={runToken()}
          kind="import"
          title={`Generating data — ${table()}`}
          onClose={() => setRunToken('')}
        />
      </Show>

      {/* SQL output */}
      <Show when={sqlOut()}>
        <Modal open={true} onClose={() => setSqlOut(null)} title={`Generated SQL — ${sqlOut().rowCount} rows`} wide>
          <textarea class="input mono" readOnly rows="18" style="font-size:12px;width:100%;" value={sqlOut().sql}></textarea>
          <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:10px;">
            <button class="btn" onClick={copySql}>Copy SQL</button>
            <button class="btn btn-ghost" onClick={() => setSqlOut(null)}>Close</button>
          </div>
        </Modal>
      </Show>
    </Modal>
  );
}
